import { z } from 'zod'

import { CASE_STATUS_TRANSITIONS, type CollectionStatus, canTransition } from './collection'

/**
 * Ficha 360° e atendimento (sprint-3-spec §1 e §2): canais, resultados, regras de status do
 * atendimento, templates de mensagem e telefone. Funções puras, usadas pela API e pelo web.
 */

// --- Canais e resultados -------------------------------------------------------------------------

export const INTERACTION_CHANNELS = [
  'whatsapp',
  'phone',
  'email',
  'in_person',
  'sms',
  'other',
] as const
export type InteractionChannel = (typeof INTERACTION_CHANNELS)[number]
export const InteractionChannelSchema = z.enum(INTERACTION_CHANNELS, {
  error: 'Escolha um canal válido.',
})

export const INTERACTION_CHANNEL_LABELS: Readonly<Record<InteractionChannel, string>> = {
  whatsapp: 'WhatsApp',
  phone: 'Ligação',
  email: 'E-mail',
  in_person: 'Presencial',
  sms: 'SMS',
  other: 'Outro',
}

export const INTERACTION_OUTCOMES = [
  'contacted',
  'no_answer',
  'invalid_phone',
  'promise',
  'agreement',
  'refused',
  'renegotiation',
  'boleto_sent',
  'other',
] as const
export type InteractionOutcome = (typeof INTERACTION_OUTCOMES)[number]
export const InteractionOutcomeSchema = z.enum(INTERACTION_OUTCOMES, {
  error: 'Escolha um resultado válido.',
})

/**
 * Resultados que contam como contato efetivo (Sprint 16.1, D17): falou com o cliente. Só eles
 * atualizam o "último contato" do caso (`lastContactAt`); "Não atendeu", "Telefone inválido",
 * "Boleto enviado" e "Outro" atualizam só o último atendimento (`lastInteractionAt`).
 */
export const EFFECTIVE_CONTACT_OUTCOMES = [
  'contacted',
  'promise',
  'agreement',
  'refused',
  'renegotiation',
] as const satisfies readonly InteractionOutcome[]

export function isEffectiveContact(outcome: InteractionOutcome): boolean {
  return (EFFECTIVE_CONTACT_OUTCOMES as readonly string[]).includes(outcome)
}

export const INTERACTION_OUTCOME_LABELS: Readonly<Record<InteractionOutcome, string>> = {
  contacted: 'Contato feito',
  no_answer: 'Não atendeu',
  invalid_phone: 'Telefone inválido',
  promise: 'Promessa de pagamento',
  agreement: 'Acordo',
  refused: 'Recusou',
  renegotiation: 'Renegociação',
  boleto_sent: 'Boleto enviado',
  other: 'Outro',
}

export const INTERACTION_OUTCOME_DESCRIPTIONS: Readonly<Record<InteractionOutcome, string>> = {
  contacted: 'Falou com o cliente ou com o responsável.',
  no_answer: 'Chamou e ninguém respondeu.',
  invalid_phone: 'O número não é do cliente ou não existe.',
  promise: 'O cliente prometeu pagar um valor numa data.',
  agreement: 'Condições de acordo combinadas.',
  refused: 'O cliente não quer regularizar agora.',
  renegotiation: 'Pediu novas condições para pagar.',
  boleto_sent: 'Segunda via ou chave PIX enviada.',
  other: 'Nenhum dos anteriores.',
}

/** Resultados que aceitam valor e data de promessa. */
export const PROMISE_OUTCOMES = ['promise', 'agreement'] as const satisfies InteractionOutcome[]

export const MESSAGE_TEMPLATE_CHANNELS = ['whatsapp', 'sms', 'email'] as const
export type MessageTemplateChannel = (typeof MESSAGE_TEMPLATE_CHANNELS)[number]
export const MessageTemplateChannelSchema = z.enum(MESSAGE_TEMPLATE_CHANNELS, {
  error: 'Use whatsapp, sms ou email.',
})

/**
 * Público do template (Sprint 17): `collection` (cobrança: ficha, fila, régua, campanhas) ou
 * `sales` (leads do CRM de vendas). Um nunca aparece nas telas do outro.
 */
export const MESSAGE_TEMPLATE_AUDIENCES = ['collection', 'sales'] as const
export type MessageTemplateAudience = (typeof MESSAGE_TEMPLATE_AUDIENCES)[number]
export const MessageTemplateAudienceSchema = z.enum(MESSAGE_TEMPLATE_AUDIENCES, {
  error: 'Use collection ou sales.',
})
export const MESSAGE_TEMPLATE_AUDIENCE_LABELS: Readonly<Record<MessageTemplateAudience, string>> = {
  collection: 'Cobrança',
  sales: 'Vendas',
}

/**
 * Tipo do evento de mensagem (sprint-6-spec §0): `opened` (WhatsApp aberto) e `copied` (texto
 * copiado) são os únicos de WhatsApp/SMS; `sent` só nasce do MailService com o id do transporte;
 * `failed` é a rejeição síncrona do SMTP; `bounced` fica reservado para o webhook futuro.
 */
export const MESSAGE_EVENT_KINDS = ['opened', 'copied', 'sent', 'failed', 'bounced'] as const
export type MessageEventKind = (typeof MESSAGE_EVENT_KINDS)[number]
export const MessageEventKindSchema = z.enum(MESSAGE_EVENT_KINDS)

/** Onde o botão do WhatsApp abre a conversa (preferência do usuário). */
export const WHATSAPP_TARGETS = ['wame', 'web', 'app'] as const
export type WhatsappTarget = (typeof WHATSAPP_TARGETS)[number]
export const WhatsappTargetSchema = z.enum(WHATSAPP_TARGETS, { error: 'Use wame, web ou app.' })

// --- Regras de status do atendimento ------------------------------------------------------------

/**
 * Status que o resultado sugere a partir do status atual, ou `null` (mantém). `promise` sugere
 * `promessa_pagamento`; `contacted` e `no_answer` tiram o caso de `novo` para `em_contato`. A
 * sugestão só vale quando a transição é válida.
 */
export function suggestedStatusFor(
  outcome: InteractionOutcome,
  current: CollectionStatus,
): CollectionStatus | null {
  let target: CollectionStatus | null = null
  if (outcome === 'promise') target = 'promessa_pagamento'
  else if ((outcome === 'contacted' || outcome === 'no_answer') && current === 'novo') {
    target = 'em_contato'
  }
  if (target === null || target === current || !canTransition(current, target)) return null
  return target
}

export type InteractionStatusPlan =
  | { ok: true; statusAfter: CollectionStatus | null }
  | { ok: false; from: CollectionStatus; options: readonly CollectionStatus[] }

/**
 * Status final do atendimento: `newStatus` explícito precisa ser transição válida (igual ao atual
 * conta como "mantém"); sem ele, aplica a sugestão do resultado. `statusAfter: null` = sem mudança.
 */
export function planInteractionStatus(
  outcome: InteractionOutcome,
  current: CollectionStatus,
  newStatus?: CollectionStatus | null,
): InteractionStatusPlan {
  if (newStatus === undefined || newStatus === null) {
    return { ok: true, statusAfter: suggestedStatusFor(outcome, current) }
  }
  if (newStatus === current) return { ok: true, statusAfter: null }
  if (!canTransition(current, newStatus)) {
    return { ok: false, from: current, options: CASE_STATUS_TRANSITIONS[current] }
  }
  return { ok: true, statusAfter: newStatus }
}

/**
 * Promessa do caso depois do atendimento: substitui quando o atendimento traz promessa; limpa
 * quando o caso sai de `promessa_pagamento` sem nova promessa; senão mantém (`undefined`).
 */
export function promiseEffect(input: {
  promisedAmount?: string | null
  promisedAt?: string | null
  statusBefore: CollectionStatus
  statusFinal: CollectionStatus
}): { promisedAmount: string; promisedAt: string } | null | undefined {
  if (input.promisedAmount && input.promisedAt) {
    return { promisedAmount: input.promisedAmount, promisedAt: input.promisedAt }
  }
  if (input.statusBefore === 'promessa_pagamento' && input.statusFinal !== 'promessa_pagamento') {
    return null
  }
  return undefined
}

// --- Telefone ------------------------------------------------------------------------------------

/**
 * Telefone brasileiro só com dígitos: sem o 0 de longa distância e sem o 55 do país. Não valida.
 */
export function phoneDigits(raw: string): string {
  let digits = raw.replace(/\D/g, '').replace(/^0+/, '')
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith('55')) {
    digits = digits.slice(2)
  }
  return digits
}

/** Válido com 10 ou 11 dígitos e DDD de 11 a 99 (o mesmo critério da carga do ERP). */
export function isValidPhoneDigits(digits: string): boolean {
  if (!/^\d{10,11}$/.test(digits)) return false
  const ddd = Number(digits.slice(0, 2))
  return ddd >= 11 && ddd <= 99
}

/** `(DD) 9XXXX-XXXX` ou `(DD) XXXX-XXXX`; outro formato volta como veio. */
export function formatPhoneDigits(digits: string): string {
  if (digits.length === 11)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
  if (digits.length === 10)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
  return digits
}

/**
 * Link de ligação (`tel:+55DDDNÚMERO`) para telefone brasileiro de 10 ou 11 dígitos, com a mesma
 * normalização do WhatsApp. Telefone inválido, sem DDD ou mascarado devolve `null`.
 */
export function telHref(phone: string | null | undefined): string | null {
  if (!phone || phone.includes('*')) return null
  const digits = phoneDigits(phone)
  return isValidPhoneDigits(digits) ? `tel:+55${digits}` : null
}

export interface WhatsappUrls {
  /** `https://wa.me/55<telefone>`: abre a conversa direto no app (celular ou desktop) ou no navegador. */
  waUrlWame: string | null
  waUrlWeb: string | null
  waUrlApp: string | null
}

/**
 * Links do WhatsApp para o telefone do cliente. Telefone inválido ou sem DDD não gera link
 * (`null`): nunca existe número de reserva.
 */
export function whatsappUrls(phone: string | null | undefined, text: string): WhatsappUrls {
  const digits = phone ? phoneDigits(phone) : ''
  if (!isValidPhoneDigits(digits)) return { waUrlWame: null, waUrlWeb: null, waUrlApp: null }
  const query = `phone=55${digits}&text=${encodeURIComponent(text)}`
  return {
    waUrlWame: `https://wa.me/55${digits}?text=${encodeURIComponent(text)}`,
    waUrlWeb: `https://web.whatsapp.com/send?${query}`,
    waUrlApp: `whatsapp://send?${query}`,
  }
}

// --- Templates de mensagem ----------------------------------------------------------------------

export const TEMPLATE_VARIABLES = [
  'primeiroNome',
  'nome',
  'operadora',
  'empresa',
  'matricula',
  'parcelasVencidas',
  'valorVencido',
  'vencimentoMaisAntigo',
  'diasAtraso',
  'valorPrometido',
  'dataPromessa',
  'proximaAcao',
  'valorOriginal',
  'valorNegociado',
  'descontoTexto',
  'parcelasAcordo',
  'primeiroVencimento',
  'numeroParcela',
  'valorParcela',
  'vencimentoParcela',
  /** Próxima parcela do ERP a vencer (Sprint 6). */
  'vencimentoProximo',
  /** Assinatura do e-mail: "Equipe de atendimento da {empresa}" (Sprint 6). */
  'assinatura',
] as const
export type TemplateVariable = (typeof TEMPLATE_VARIABLES)[number]
export type TemplateValues = Partial<Record<TemplateVariable, string | number | null | undefined>>

/**
 * Rótulo curto de cada variável (o chip do editor). O formato de exemplo fica em
 * `TEMPLATE_VARIABLE_EXAMPLES` (o tooltip do chip), nunca no rótulo.
 */
export const TEMPLATE_VARIABLE_LABELS: Readonly<Record<TemplateVariable, string>> = {
  primeiroNome: 'Primeiro nome do cliente',
  nome: 'Nome completo do cliente',
  operadora: 'Seu nome',
  empresa: 'Nome da empresa',
  matricula: 'Matrícula',
  parcelasVencidas: 'Quantidade de parcelas vencidas',
  valorVencido: 'Valor vencido',
  vencimentoMaisAntigo: 'Vencimento mais antigo',
  diasAtraso: 'Dias de atraso',
  valorPrometido: 'Valor prometido',
  dataPromessa: 'Data da promessa',
  proximaAcao: 'Data da próxima ação',
  valorOriginal: 'Valor original do acordo',
  valorNegociado: 'Valor negociado do acordo',
  descontoTexto: 'Desconto do acordo',
  parcelasAcordo: 'Plano do acordo',
  primeiroVencimento: 'Primeiro vencimento do acordo',
  numeroParcela: 'Número da próxima parcela do acordo',
  valorParcela: 'Valor da próxima parcela do acordo',
  vencimentoParcela: 'Vencimento da próxima parcela do acordo',
  vencimentoProximo: 'Vencimento da próxima parcela a vencer',
  assinatura: 'Assinatura do e-mail',
}

const VARIABLE_PATTERN = /\{(\w+)\}/g
const KNOWN_VARIABLES = new Set<string>(TEMPLATE_VARIABLES)

/**
 * Troca `{variavel}` pelo valor; variável sem valor vira texto vazio e a frase é limpa: espaços
 * repetidos viram um, espaço antes de pontuação some e vírgula antes de ponto final cai.
 * Chaves desconhecidas ficam como estão.
 */
export function renderTemplate(body: string, values: TemplateValues): string {
  const replaced = body.replace(VARIABLE_PATTERN, (match, name: string) => {
    if (!KNOWN_VARIABLES.has(name)) return match
    const value = values[name as TemplateVariable]
    return value === null || value === undefined ? '' : String(value).trim()
  })
  return replaced
    .split('\n')
    .map((line) =>
      line
        .replace(/[ \t]{2,}/g, ' ')
        .replace(/[ \t]+([,.!?;:])/g, '$1')
        .replace(/,([.!?])/g, '$1')
        .replace(/[ \t]+$/g, '')
        .replace(/^[ \t]+/g, ''),
    )
    .join('\n')
    .trim()
}

/** Primeira letra de cada palavra em maiúscula; partículas (da, de, dos...) em minúsculas. */
export function titleCaseName(name: string): string {
  const particles = new Set(['da', 'das', 'de', 'do', 'dos', 'e'])
  return name
    .trim()
    .toLocaleLowerCase('pt-BR')
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index) =>
      index > 0 && particles.has(word)
        ? word
        : word.charAt(0).toLocaleUpperCase('pt-BR') + word.slice(1),
    )
    .join(' ')
}

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

/** `"1234.5"` → `R$ 1.234,50` (espaço comum depois do símbolo). */
export function formatBrl(amount: string | number): string {
  return brl.format(Number(amount)).replace(/\s/g, ' ')
}

/** `AAAA-MM-DD` → `DD/MM/AAAA`. */
export function formatIsoDateBr(date: string): string {
  const [year, month, day] = date.slice(0, 10).split('-')
  return `${day}/${month}/${year}`
}

/** Faixa de atraso e status sugeridos de um template. */
export interface TemplateSuggestionRule {
  id: string
  suggestedFor: readonly CollectionStatus[]
  minDaysOverdue?: number | null
  maxDaysOverdue?: number | null
  sortOrder: number
}

/**
 * Template sugerido: o primeiro (por `sortOrder`) cujo `suggestedFor` contém o status e cuja faixa
 * de atraso (limites inclusivos; ausente = aberta) contém os dias. Nenhum → `null`.
 */
export function suggestTemplate<T extends TemplateSuggestionRule>(
  templates: readonly T[],
  status: CollectionStatus,
  daysOverdue: number,
): T | null {
  const sorted = [...templates].sort((a, b) => a.sortOrder - b.sortOrder)
  return (
    sorted.find(
      (template) =>
        template.suggestedFor.includes(status) &&
        (template.minDaysOverdue ?? Number.NEGATIVE_INFINITY) <= daysOverdue &&
        daysOverdue <= (template.maxDaysOverdue ?? Number.POSITIVE_INFINITY),
    ) ?? null
  )
}

export interface MessageTemplateSeed {
  key: string
  name: string
  channel: MessageTemplateChannel
  /** Assunto (só e-mail). */
  subject?: string
  description?: string
  body: string
  suggestedFor: CollectionStatus[]
  minDaysOverdue: number | null
  maxDaysOverdue: number | null
  sortOrder: number
  /** Ausente = `collection`. */
  audience?: MessageTemplateAudience
}

/**
 * Templates iniciais (sprint-3-spec §1), gravados pelo seed só quando a chave não existe. Seguem o
 * CDC: sem ameaça nem constrangimento, identificam a operadora e a empresa e oferecem um caminho.
 */
export const MESSAGE_TEMPLATE_SEEDS: readonly MessageTemplateSeed[] = Object.freeze([
  {
    key: 'lembrete_amigavel',
    name: 'Lembrete amigável',
    channel: 'whatsapp',
    body: 'Olá, {primeiroNome}! Aqui é {operadora}, da {empresa}. Identificamos uma parcela do seu plano com vencimento em {vencimentoMaisAntigo} ainda em aberto. Posso te enviar a segunda via ou a chave PIX para regularizar?',
    suggestedFor: ['novo', 'em_contato'],
    minDaysOverdue: null,
    maxDaysOverdue: 30,
    sortOrder: 10,
  },
  {
    key: 'atraso_acumulado',
    name: 'Atraso acumulado',
    channel: 'whatsapp',
    body: 'Olá, {primeiroNome}. Aqui é {operadora}, da {empresa}. Constam {parcelasVencidas} parcela(s) em aberto no seu plano, somando {valorVencido}, a mais antiga de {vencimentoMaisAntigo}. Temos condições facilitadas para regularizar. Qual o melhor horário para conversarmos?',
    suggestedFor: ['novo', 'em_contato', 'sem_contato'],
    minDaysOverdue: 31,
    maxDaysOverdue: null,
    sortOrder: 20,
  },
  {
    key: 'retorno_combinado',
    name: 'Retorno combinado',
    channel: 'whatsapp',
    body: 'Olá, {primeiroNome}! Aqui é {operadora}, da {empresa}. Conforme combinamos, estou retornando sobre a regularização do seu plano. Se o pagamento já foi feito, pode me enviar o comprovante por aqui que eu confirmo a baixa.',
    suggestedFor: ['em_negociacao', 'promessa_pagamento'],
    minDaysOverdue: null,
    maxDaysOverdue: null,
    sortOrder: 30,
  },
  {
    key: 'sem_contato',
    name: 'Tentativa de contato',
    channel: 'whatsapp',
    body: 'Olá, {primeiroNome}. Aqui é {operadora}, da {empresa}. Tentei falar com você sobre o seu plano e não consegui. Quando puder, me responda por aqui ou me diga o melhor horário para eu ligar.',
    suggestedFor: ['sem_contato'],
    minDaysOverdue: null,
    maxDaysOverdue: null,
    sortOrder: 40,
  },
  {
    key: 'proposta_acordo',
    name: 'Proposta de acordo',
    channel: 'whatsapp',
    body: 'Olá, {primeiroNome}! Aqui é {operadora}, da {empresa}. Conforme conversamos, segue a proposta para regularizar o seu plano: {parcelasAcordo} a partir de {primeiroVencimento}, totalizando {valorNegociado}{descontoTexto}. Posso confirmar e te enviar a forma de pagamento?',
    suggestedFor: ['em_negociacao', 'promessa_pagamento', 'acordo_quebrado'],
    minDaysOverdue: null,
    maxDaysOverdue: null,
    sortOrder: 50,
  },
  {
    key: 'lembrete_parcela_acordo',
    name: 'Lembrete de parcela do acordo',
    channel: 'whatsapp',
    body: 'Olá, {primeiroNome}! Aqui é {operadora}, da {empresa}. Passando para lembrar que a parcela {numeroParcela} do seu acordo, de {valorParcela}, vence em {vencimentoParcela}. Qualquer dúvida, é só me responder por aqui.',
    suggestedFor: ['acordo_firmado', 'acordo_em_andamento'],
    minDaysOverdue: null,
    maxDaysOverdue: null,
    sortOrder: 60,
  },
  // Sprint 6 (sprint-6-spec §1): os 5 do sistema antigo, reescritos pelo CDC. Vêm depois dos
  // existentes na ordem de sugestão; a régua é quem sugere `atraso_firme` pela etapa.
  {
    key: 'atraso_firme',
    name: 'Cobrança firme',
    channel: 'whatsapp',
    body: 'Olá, {primeiroNome}. Aqui é {operadora}, da {empresa}. Seu plano está com {parcelasVencidas} parcela(s) em aberto desde {vencimentoMaisAntigo}, somando {valorVencido}. Para manter os benefícios do plano ativos, podemos regularizar hoje com condições facilitadas. Qual o melhor horário para conversarmos?',
    suggestedFor: ['novo', 'em_contato', 'sem_contato', 'recorrente'],
    minDaysOverdue: 46,
    maxDaysOverdue: null,
    sortOrder: 70,
  },
  {
    key: 'boleto_disponivel',
    name: 'Boleto disponível',
    channel: 'whatsapp',
    body: 'Olá, {primeiroNome}! Aqui é {operadora}, da {empresa}. O boleto da sua parcela com vencimento em {vencimentoProximo} já está disponível. Quer que eu envie a segunda via por aqui?',
    suggestedFor: ['em_negociacao', 'promessa_pagamento'],
    minDaysOverdue: null,
    maxDaysOverdue: null,
    sortOrder: 80,
  },
  {
    key: 'sms_pendencia',
    name: 'SMS de pendência',
    channel: 'sms',
    body: '{empresa}: {primeiroNome}, sua parcela de {vencimentoMaisAntigo} está em aberto. Fale com {operadora} por este número para regularizar.',
    suggestedFor: ['novo', 'em_contato', 'sem_contato'],
    minDaysOverdue: null,
    maxDaysOverdue: null,
    sortOrder: 90,
  },
  {
    key: 'email_aviso_atraso',
    name: 'Aviso de parcela em aberto',
    channel: 'email',
    subject: 'Parcela em aberto no seu plano {empresa}',
    body: 'Olá, {primeiroNome}.\n\nIdentificamos {parcelasVencidas} parcela(s) do seu plano em aberto desde {vencimentoMaisAntigo}, somando {valorVencido}. Podemos regularizar com condições facilitadas: responda este e-mail ou fale com {operadora}.\n\nSe o pagamento já foi feito, desconsidere esta mensagem e, se puder, nos envie o comprovante.\n\n{assinatura}\n\nPara não receber mais avisos por e-mail, responda com a palavra SAIR.',
    suggestedFor: ['novo', 'sem_contato'],
    minDaysOverdue: 60,
    maxDaysOverdue: null,
    sortOrder: 100,
  },
  {
    key: 'email_lembrete_acordo',
    name: 'Lembrete de parcela do acordo por e-mail',
    channel: 'email',
    subject: 'Parcela {numeroParcela} do seu acordo vence em {vencimentoParcela}',
    body: 'Olá, {primeiroNome}.\n\nA parcela {numeroParcela} do seu acordo, de {valorParcela}, vence em {vencimentoParcela}. Qualquer dúvida, responda este e-mail.\n\n{assinatura}',
    suggestedFor: ['acordo_firmado', 'acordo_em_andamento'],
    minDaysOverdue: null,
    maxDaysOverdue: null,
    sortOrder: 110,
  },
])

/** Chaves dos templates de vendas (Sprint 17). */
export const SALES_FIRST_CONTACT_TEMPLATE_KEY = 'vendas_primeiro_contato'
export const SALES_PROPOSAL_FOLLOWUP_TEMPLATE_KEY = 'vendas_retorno_proposta'

/**
 * Templates de vendas (sprint-17-spec §2, `audience = sales`), gravados pelo seed só quando a chave
 * não existe. Seguem o CDC como os de cobrança: identificam quem fala e a empresa, sem pressão.
 */
export const SALES_TEMPLATE_SEEDS: readonly MessageTemplateSeed[] = Object.freeze([
  {
    key: SALES_FIRST_CONTACT_TEMPLATE_KEY,
    name: 'Primeiro contato',
    channel: 'whatsapp',
    audience: 'sales',
    description: 'Primeira mensagem ao lead (o ícone do WhatsApp no cartão usa este).',
    body: 'Olá, {primeiroNome}! Aqui é {operadora}, da {empresa}. Recebi o seu interesse nos nossos planos e queria entender o que você procura. Posso te passar as opções e os valores por aqui?',
    suggestedFor: [],
    minDaysOverdue: null,
    maxDaysOverdue: null,
    sortOrder: 500,
  },
  {
    key: SALES_PROPOSAL_FOLLOWUP_TEMPLATE_KEY,
    name: 'Retorno da proposta',
    channel: 'whatsapp',
    audience: 'sales',
    description: 'Retorno depois de enviar a proposta.',
    body: 'Olá, {primeiroNome}! Aqui é {operadora}, da {empresa}. Estou retornando sobre a proposta que conversamos. Ficou alguma dúvida? Se preferir, me diga o melhor horário para conversarmos.',
    suggestedFor: [],
    minDaysOverdue: null,
    maxDaysOverdue: null,
    sortOrder: 510,
  },
])

/** Chaves dos templates de acordo (Sprint 4). */
export const AGREEMENT_PROPOSAL_TEMPLATE_KEY = 'proposta_acordo'
export const AGREEMENT_REMINDER_TEMPLATE_KEY = 'lembrete_parcela_acordo'
